import semverSatisfies from 'semver/functions/satisfies'
import {
  getSafeSingletonDeployment,
  getSafeL2SingletonDeployment,
  getMultiSendCallOnlyDeployment,
  getFallbackHandlerDeployment,
  getProxyFactoryDeployment,
  getSignMessageLibDeployment,
} from '@safe-global/safe-deployments'
import type { SingletonDeployment, DeploymentFilter } from '@safe-global/safe-deployments'
import type { ChainInfo, SafeInfo } from '@safe-global/safe-gateway-typescript-sdk'

import { LATEST_SAFE_VERSION } from '@/config/constants'

// SAFE4 change: contract address overrides carried on a (custom) chain config, supplied via the
// magic-network URL params. Undefined on registered chains, where safe-deployments resolves them.
type ChainWithContractOverrides = ChainInfo & {
  safeSingletonAddress?: string
  safeProxyFactoryAddress?: string
  fallbackHandlerAddress?: string
}

export const _tryDeploymentVersions = (
  getDeployment: (filter?: DeploymentFilter) => SingletonDeployment | undefined,
  network: string,
  version: SafeInfo['version'],
): SingletonDeployment | undefined => {
  // Unsupported Safe version
  if (version === null) {
    // Assume latest version as fallback
    return getDeployment({
      version: LATEST_SAFE_VERSION,
      network,
    })
  }

  // Supported Safe version
  return getDeployment({
    version,
    network,
  })
}

// Reuse the canonical deployment's ABI but point it at an operator-provided address, so custom
// chains that aren't in @safe-global/safe-deployments still resolve a usable contract deployment.
const _withOverrideAddress = (
  getDeployment: (filter?: DeploymentFilter) => SingletonDeployment | undefined,
  chainId: string,
  version: SafeInfo['version'],
  overrideAddress: string,
): SingletonDeployment | undefined => {
  const base =
    _tryDeploymentVersions(getDeployment, chainId, version) ??
    getDeployment({ version: version ?? LATEST_SAFE_VERSION }) ??
    getDeployment({ version: LATEST_SAFE_VERSION })
  if (!base) return undefined
  return {
    ...base,
    defaultAddress: overrideAddress,
    networkAddresses: { ...base.networkAddresses, [chainId]: overrideAddress },
  }
}

export const _isLegacy = (safeVersion: SafeInfo['version']): boolean => {
  const LEGACY_VERSIONS = '<=1.0.0'
  return !!safeVersion && semverSatisfies(safeVersion, LEGACY_VERSIONS)
}

export const _isL2 = (chain: ChainInfo, safeVersion: SafeInfo['version']): boolean => {
  const L2_VERSIONS = '>=1.3.0'

  // Unsupported safe version
  if (safeVersion === null) {
    return chain.l2
  }

  // We had L1 contracts on xDai, EWC and Volta so we also need to check version is after 1.3.0
  return chain.l2 && semverSatisfies(safeVersion, L2_VERSIONS)
}

export const getSafeContractDeployment = (
  chain: ChainInfo,
  safeVersion: SafeInfo['version'],
): SingletonDeployment | undefined => {
  // Check if prior to 1.0.0 to keep minimum compatibility
  if (_isLegacy(safeVersion)) {
    return getSafeSingletonDeployment({ version: '1.0.0' })
  }

  const getDeployment = _isL2(chain, safeVersion) ? getSafeL2SingletonDeployment : getSafeSingletonDeployment

  const override = (chain as ChainWithContractOverrides).safeSingletonAddress
  if (override) {
    return _withOverrideAddress(getDeployment, chain.chainId, safeVersion, override)
  }

  return _tryDeploymentVersions(getDeployment, chain.chainId, safeVersion)
}

export const getMultiSendCallOnlyContractDeployment = (chainId: string, safeVersion: SafeInfo['version']) => {
  return _tryDeploymentVersions(getMultiSendCallOnlyDeployment, chainId, safeVersion)
}

export const getFallbackHandlerContractDeployment = (chain: ChainInfo, safeVersion: SafeInfo['version']) => {
  const override = (chain as ChainWithContractOverrides).fallbackHandlerAddress
  if (override) {
    return _withOverrideAddress(getFallbackHandlerDeployment, chain.chainId, safeVersion, override)
  }
  return _tryDeploymentVersions(getFallbackHandlerDeployment, chain.chainId, safeVersion)
}

export const getProxyFactoryContractDeployment = (chain: ChainInfo, safeVersion: SafeInfo['version']) => {
  const override = (chain as ChainWithContractOverrides).safeProxyFactoryAddress
  if (override) {
    return _withOverrideAddress(getProxyFactoryDeployment, chain.chainId, safeVersion, override)
  }
  return _tryDeploymentVersions(getProxyFactoryDeployment, chain.chainId, safeVersion)
}

export const getSignMessageLibContractDeployment = (chainId: string, safeVersion: SafeInfo['version']) => {
  return _tryDeploymentVersions(getSignMessageLibDeployment, chainId, safeVersion)
}
